import "./PaintingDetails.css";

import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import Footer1 from "../components/Footer1";
import { motion } from "framer-motion";
import { usePageSEO } from "../components/SEOManager";

// Set REACT_APP_API_BASE_URL to your REAL production backend address before
// building/deploying the website. The fallback only preserves local development.
const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8080"
).replace(/\/+$/, "");

const SITE_ORIGIN = "https://anjaanvivek.com";

function toPlainText(value) {
  return String(value || "")
    .replace(/<br\s*\/?\s*>/gi, " ")
    .replace(/<\/(?:p|h[1-6]|li|div)>/gi, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim();
}

function shorten(text, length = 160) {
  const value = String(text || "").replace(/\s+/g, " ").trim();
  if (value.length <= length) return value;
  return value.slice(0, length - 1).replace(/\s+\S*$/, "").trimEnd() + "…";
}

function getArtworkImage(artwork) {
  return (
    artwork?.images?.find((image) => image.imageType === "BASE")?.imageUrl ||
    artwork?.images?.[0]?.imageUrl ||
    null
  );
}

function buildArtworkSEO(artwork, id) {
  const title = String(artwork.title || "Original Painting").trim();
  const canonicalPath = `/paintingDetails/${encodeURIComponent(id)}`;
  const canonicalURL = `${SITE_ORIGIN}${canonicalPath}`;
  const image = getArtworkImage(artwork);
  const story = toPlainText(artwork.story);
  const description = shorten(
    `${title} by Anjaan Vivek. ${story || `Discover the story, symbolism and details of this original ${artwork.material || "painting"}.`}`
  );
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "VisualArtwork",
    "@id": `${canonicalURL}#artwork`,
    name: title,
    description,
    url: canonicalURL,
    artform: "Painting",
    creator: {
      "@type": "Person",
      "@id": `${SITE_ORIGIN}/#artist`,
      name: "Anjaan Vivek",
      url: `${SITE_ORIGIN}/`,
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonicalURL },
    ...(image ? { image } : {}),
    ...(artwork.material ? { artMedium: artwork.material } : {}),
    ...(artwork.year ? { dateCreated: String(artwork.year) } : {}),
    ...(artwork.series
      ? { isPartOf: { "@type": "CreativeWorkSeries", name: artwork.series } }
      : {}),
  };

  return {
    title: `${title} | Anjaan Vivek`,
    description,
    canonicalPath,
    image: image || undefined,
    imageAlt: `${title}, original painting by Anjaan Vivek`,
    structuredData,
  };
}

const PaintingDetails = () => {
  const { id } = useParams();
  const { pathname } = useLocation();

  const [painting, setPainting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openDropdowns, setOpenDropdowns] = useState([]);
  const [readMore, setReadMore] = useState(false);
  const [paintingIndex, setPaintingIndex] = useState(0);
  const [paintings, setPaintings] = useState([]);
  const [hoveredPainting, setHoveredPainting] = useState(null);
  const [wishlist, setWishlist] = useState([]);

  // IMPORTANT: Hooks must run on every render (before any early return).
  // Check the ID so previous artwork metadata never leaks onto a new URL.
  const activePainting =
    painting && String(painting.id) === String(id) && !loading && !error
      ? painting
      : null;

  const artworkSEO = useMemo(() => {
    if (activePainting) return buildArtworkSEO(activePainting, id);
    // Avoid indexing an error page as if it were an actual painting.
    if (!loading && (error || !painting)) {
      return {
        title: "Artwork Unavailable | Anjaan Vivek",
        description: "This artwork is currently unavailable.",
        noindex: true,
      };
    }
    return null;
  }, [activePainting, id, loading, error, painting]);
  usePageSEO(artworkSEO);

  const storyText = useMemo(
    () => toPlainText(activePainting?.story),
    [activePainting]
  );

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // Fetch other paintings for the related artworks section.
  useEffect(() => {
    let active = true;
    const fetchPaintings = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/paintings/all`);
        if (active) setPaintings(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Error fetching paintings:", err);
      }
    };
    fetchPaintings();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setPainting(null);
    setLoading(true);
    setError("");
    setPaintingIndex(0);
    setReadMore(false);

    const fetchPainting = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/paintings/${encodeURIComponent(id)}`,
          { signal: controller.signal }
        );
        if (!res.ok) throw new Error("Failed to fetch masterpiece details");
        const data = await res.json();
        if (active) setPainting(data);
      } catch (err) {
        if (active && err.name !== "AbortError") setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchPainting();
    fetchWishlist();
    return () => {
      active = false;
      controller.abort();
    };
  }, [id]);

  const fetchWishlist = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      const res = await axios.get(`${API_BASE_URL}/api/wishlist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const wishlistIds = res.data.map((item) => item.painting.id);
      setWishlist(wishlistIds);
    } catch (err) {
      console.error("Error fetching wishlist:", err);
    }
  };

  const handleAddToWishlist = async (paintingId) => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      alert("Please log in to save items to your private collection.");
      return;
    }
    try {
      await axios.post(
        `${API_BASE_URL}/api/wishlist/add/${paintingId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWishlist((prev) =>
        prev.includes(paintingId)
          ? prev.filter((itemId) => itemId !== paintingId)
          : [...prev, paintingId]
      );
    } catch (err) {
      console.error("Error updating collection:", err);
    }
  };

  const handlePrivateInquiry = () => {
    const message = encodeURIComponent(
      `Dear Team,\n\nI am interested in acquiring the signature masterpiece titled "${painting?.title}" by Anjaan Vivek. I would like to request private acquisition pricing, availability details, and delivery provisions.\n\nThank you.`
    );
    window.open(`https://wa.me/919300847841?text=${message}`, "_blank", "noopener,noreferrer");
  };

  const toggleDropdown = (section) => {
    setOpenDropdowns((prev) =>
      prev.includes(section)
        ? prev.filter((item) => item !== section)
        : [...prev, section]
    );
  };

  const isOpen = (section) => openDropdowns.includes(section);
  const formatName = (name) =>
    name?.toLowerCase().split(" ").map(
      (word) => word.charAt(0).toUpperCase() + word.slice(1)
    ).join(" ");

  if (loading) return <p className="premium-loader">Unveiling Masterpiece Details...</p>;
  if (error) return <p>Error: {error}</p>;
  if (!painting) return <p>Masterpiece not found.</p>;

  const care_str = "Each original curation requires temperature-controlled mounting away from sustained high-humidity conditions. Clean lightly using anti-static micro-fiber sweeps without fluid interfaces.";
  const provenance_str = "This investment-grade asset ships directly from the studio of the artist accompanied by a bespoke Certificate of Authenticity signed by Anjaan Vivek, globally documented catalog entries, and secure white-glove art courier wrapping.";
  const galleryImages = Array.isArray(painting.images) ? painting.images : [];
  const mainImage = galleryImages[paintingIndex]?.imageUrl || getArtworkImage(painting);
  const bannerImage = galleryImages[2]?.imageUrl || getArtworkImage(painting);

  return (
    <>
      <div style={{ backgroundColor: "#ffffff" }}>
        <div
          className="top_banner"
          style={{ backgroundImage: bannerImage ? `url(${bannerImage})` : undefined }}
        >
          <h1>The Collection</h1>
          <p>Exclusive Original Masterpieces by Anjaan Vivek</p>
        </div>

        <div className="painting_container">
          {/* Main artwork gallery — keep existing styling, add a real img for Google Images. */}
          <div className="big_image">
            <div
              className="painting_img premium-frame"
              style={{ backgroundImage: mainImage ? `url(${mainImage})` : undefined }}
            >
              {mainImage && (
                <img
                  src={mainImage}
                  alt={`${painting.title}, original painting by Anjaan Vivek`}
                  loading="eager"
                  fetchPriority="high"
                  style={{ display: "block", width: "100%", height: "100%", objectFit: "contain" }}
                />
              )}
            </div>

            <div className="painting_options">
              {galleryImages.slice(0, 2).map((img, idx) => (
                <div
                  key={img.id ?? idx}
                  className="painting_option1"
                  style={{
                    backgroundImage: `url(${img.imageUrl})`,
                    borderColor: paintingIndex === idx ? "#88b019" : "#e2e8f0",
                  }}
                  onClick={() => setPaintingIndex(idx)}
                  role="button"
                  tabIndex={0}
                  aria-label={`View image ${idx + 1} of ${painting.title}`}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setPaintingIndex(idx);
                    }
                  }}
                />
              ))}
            </div>
          </div>

          <motion.div className="details-right" initial="hidden" animate="visible" variants={fadeIn}>
            <span className="artist-label">Original Work By {formatName(painting.artist)}</span>
            <h1 className="art-title">{painting.title}</h1>

            <div className="specs-row">
              <span>{painting.size || "Dimensions Available Upon Request"}</span>
              <span className="separator">•</span>
              <span>{painting.material || "Fine Art Media"}</span>
              <span className="separator">•</span>
              <span className="status-badge">{painting.sold ? "Acquired" : "Available"}</span>
            </div>

            <p className="premium-price-status">
              {painting.sold ? "Private Collection" : "Pricing Available Upon Request"}
            </p>

            <div className="action-buttons-group luxury-actions">
              {!painting.sold ? (
                <button className="btn-premium-acquire" onClick={handlePrivateInquiry}>
                  <i className="fa-regular fa-envelope"></i> Request Price & Private Consultation
                </button>
              ) : (
                <button className="btn-premium-disabled" disabled>Artwork Acquired</button>
              )}

              <button
                className={`btn-wishlist-main ${wishlist.includes(painting.id) ? "active" : ""}`}
                onClick={() => handleAddToWishlist(painting.id)}
                title="Add to Private Collection"
                aria-label="Add to private collection"
              >
                <i className={`fa-${wishlist.includes(painting.id) ? "solid" : "regular"} fa-heart`}></i>
              </button>
            </div>

            <div className="art-story premium-story">
              <h3>Curator's Note & Narrative</h3>
              {/* Keep the complete story in the DOM, even when visually collapsed. */}
              <p
                id="painting-full-story"
                style={readMore ? undefined : {
                  display: "-webkit-box",
                  WebkitLineClamp: 5,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {storyText}
              </p>
              {storyText.length > 250 && (
                <button
                  type="button"
                  className="read-more-link luxury-more"
                  onClick={() => setReadMore(!readMore)}
                  aria-expanded={readMore}
                  aria-controls="painting-full-story"
                  style={{ border: 0, padding: 0, background: "transparent", cursor: "pointer" }}
                >
                  {readMore ? "Read Less" : "Read Full Narrative"}
                </button>
              )}
            </div>

            <div className="dropDown premium-dropdown">
              <div className="dropDownHeader" onClick={() => toggleDropdown("Provenance")}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <i className="fa-solid fa-certificate" style={{ color: "#88b019" }}></i>
                  <h5>Provenance & Authenticity</h5>
                </div>
                <i className={`fa-solid fa-angle-down transition-transform ${isOpen("Provenance") ? "rotate-180" : ""}`}></i>
              </div>
              <div className={`dropDownContent1 ${isOpen("Provenance") ? "open" : ""}`}>
                <p>{provenance_str}</p>
              </div>
            </div>

            <div className="dropDown premium-dropdown">
              <div className="dropDownHeader" onClick={() => toggleDropdown("Care")}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <i className="fa-regular fa-gem" style={{ color: "#88b019" }}></i>
                  <h5>Archival Care Instructions</h5>
                </div>
                <i className={`fa-solid fa-angle-down transition-transform ${isOpen("Care") ? "rotate-180" : ""}`}></i>
              </div>
              <div className={`dropDownContent1 ${isOpen("Care") ? "open" : ""}`}>
                <p>{care_str}</p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="all_paintings_div premium-related-section">
          <h4><span>Explore Further</span> Masterpieces</h4>
          <div className="all_paintings_container">
            {paintings.filter((p) => p.id !== painting.id).slice(0, 3).map((p) => (
              <Link
                to={`/paintingDetails/${p.id}`}
                key={p.id}
                className="painting_card premium-card"
                onMouseEnter={() => setHoveredPainting(p.id)}
                onMouseLeave={() => setHoveredPainting(null)}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <div
                  className="painting_image"
                  style={{
                    backgroundImage: `url(${hoveredPainting === p.id && p.images?.[1]
                      ? p.images[1].imageUrl
                      : p.images?.[0]?.imageUrl})`,
                  }}
                />
                <div className="painting_info premium-info">
                  <div>
                    <h3>{p.title}</h3>
                    <p className="premium-card-subtitle">View Artwork Details</p>
                  </div>
                  <i className="fa-solid fa-arrow-right-long text-brand"></i>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
      <Footer1 />
    </>
  );
};

export default PaintingDetails;
